import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { customerName, simTimeShort } from '@/utils/format';
import { simulationApi } from '@/events/simulationEngine';
import ConversationTimelineEntry from '@/components/ConversationTimelineEntry';
import { Sparkles, Check, X, MessageSquare, HandshakeIcon } from 'lucide-react';
import clsx from 'clsx';

const PLAYBOOK_STEPS = [
  'Greet warmly',
  'Confirm location',
  'Estimate size',
  'Suggest artist',
  'Ask deposit',
  'Send link',
];

export default function LiveAIConversations() {
  const aiConversations = useBrainStore((s) => s.aiConversations);
  const customers = useBrainStore((s) => s.customers);
  const upsertAIConversation = useBrainStore((s) => s.upsertAIConversation);
  const navigate = useNavigate();

  const list = Object.values(aiConversations);
  const [selectedId, setSelectedId] = useState<string | null>(
    list[0]?.id ?? null,
  );

  // Auto-wake the AI conversation layer on first mount when empty. Georgina
  // shouldn't land on a "no conversations yet, click this button" screen — the
  // PRD promise is "AI is talking". Trigger the canonical 11am state once.
  const wokenRef = useRef(false);
  useEffect(() => {
    if (wokenRef.current) return;
    if (list.length > 0) return;
    wokenRef.current = true;
    simulationApi.triggerEvent('aaron-meta-dm-arrives');
    simulationApi.triggerEvent('jess-meta-dm-arrives');
    simulationApi.triggerEvent('tyler-handoff-requested');
    simulationApi.triggerEvent('aaron-playbook-progress');
  }, [list.length]);

  const selected = selectedId ? aiConversations[selectedId] : undefined;
  const selectedCustomer = selected ? customers[selected.customerId] : undefined;
  const handoffs = list.filter((c) => c.state === 'hand-off-requested');

  return (
    <section className="px-8 py-8" data-testid="live-ai-conversations">
      <header className="max-w-6xl flex items-end justify-between">
        <div>
          <p className="label-small-caps text-gold-bright">PRD §12.8</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Live AI Conversations</h1>
          <p className="font-serif text-cream-soft mt-3">
            Watch the AI conversational layer in real time. Tick-cross visibility against the
            playbook. Pick up hand-offs.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <Pill label="Live" value={list.length} />
          <Pill label="Hand-offs" value={handoffs.length} accent={handoffs.length > 0} />
          <Pill label="High value" value={list.filter((c) => c.flags.highValue).length} />
        </div>
      </header>

      <div className="mt-6 grid grid-cols-12 gap-6 max-w-7xl">
        <aside className="col-span-3 space-y-2">
          <h3 className="label-small-caps text-gold-bright mb-2">Conversations</h3>
          {list.length === 0 ? (
            <p className="text-sm text-cream-soft italic">Waking AI…</p>
          ) : null}
          {list.length === 0 ? (
            <button
              type="button"
              onClick={() => {
                simulationApi.triggerEvent('aaron-meta-dm-arrives');
                simulationApi.triggerEvent('jess-meta-dm-arrives');
                simulationApi.triggerEvent('tyler-handoff-requested');
                simulationApi.triggerEvent('aaron-playbook-progress');
              }}
              className="btn-ghost text-xs w-full mt-3"
              data-testid="seed-ai-conversations"
            >
              Wake AI conversations
            </button>
          ) : null}
          {list.map((c) => {
            const cust = customers[c.customerId];
            const selectedState = selectedId === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`ai-card-${c.id}`}
                onClick={() => setSelectedId(c.id)}
                className={clsx(
                  'w-full text-left rounded-lg border p-3 transition',
                  selectedState
                    ? 'border-gold-bright bg-gold/10'
                    : 'border-gold-dim/25 hover:border-gold-bright/40',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-inter text-sm text-cream">
                    {cust ? customerName(cust) : c.customerId}
                  </p>
                  {c.state === 'hand-off-requested' ? (
                    <span className="pill-blood text-[9px]">Hand-off</span>
                  ) : c.flags.highValue ? (
                    <span className="pill-gold-solid text-[9px]">High value</span>
                  ) : null}
                </div>
                <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                  {c.channel} · {c.toneTag} · {simTimeShort(c.lastMessageAt)}
                </p>
                <div className="h-1 w-full bg-ink-soft rounded-full mt-2 overflow-hidden">
                  <div
                    className="h-full bg-gold-bright"
                    style={{ width: `${c.playbookProgressPct}%` }}
                  />
                </div>
                <p className="font-mono text-[9px] text-cream-soft mt-1">
                  {c.playbookProgressPct}% playbook
                </p>
              </button>
            );
          })}
        </aside>

        <section className="col-span-6">
          {selected && selectedCustomer ? (
            <div>
              <header className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-inter text-lg text-cream">
                    {customerName(selectedCustomer)}
                  </p>
                  <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                    {selected.channel} · {selected.toneTag} · {selected.state}
                  </p>
                </div>
                {selected.flags.highValue ? (
                  <span className="pill-gold-solid">High value</span>
                ) : null}
              </header>

              <div className="panel p-4 mb-3" data-testid="playbook-progress">
                <p className="label-small-caps">Playbook</p>
                <ol className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                  {PLAYBOOK_STEPS.map((step, idx) => {
                    const done = (idx + 1) * (100 / PLAYBOOK_STEPS.length) <= selected.playbookProgressPct;
                    return (
                      <li
                        key={step}
                        className={clsx(
                          'flex items-center gap-2 rounded-md border px-2 py-1',
                          done
                            ? 'border-mint/50 bg-mint/10 text-mint'
                            : 'border-gold-dim/30 text-cream-soft',
                        )}
                      >
                        {done ? <Check size={10} /> : <X size={10} />}
                        {step}
                      </li>
                    );
                  })}
                </ol>
              </div>

              <h3 className="label-small-caps mb-2">Thread</h3>
              {selectedCustomer.conversation.slice(-6).map((entry) => (
                <ConversationTimelineEntry key={entry.id} entry={entry} />
              ))}
            </div>
          ) : (
            <p className="font-serif text-cream-soft">
              Select a conversation to inspect.
            </p>
          )}
        </section>

        <aside className="col-span-3">
          <h3 className="label-small-caps text-gold-bright mb-2">Hand-off panel</h3>
          {handoffs.length === 0 ? (
            <p className="text-xs text-cream-soft italic">No hand-offs pending.</p>
          ) : null}
          {handoffs.map((h) => {
            const cust = customers[h.customerId];
            return (
              <div key={h.id} className="panel p-4 mb-3" data-testid={`handoff-card-${h.id}`}>
                <p className="font-inter text-sm text-cream">
                  {cust ? customerName(cust) : h.customerId}
                </p>
                <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                  Reason: {h.flags.handoffReason ?? 'unspecified'}
                </p>
                {h.handoffSuggestedOpener ? (
                  <p className="mt-3 text-xs font-serif text-gold-bright bg-gold/10 rounded p-2">
                    Suggested opener: {h.handoffSuggestedOpener}
                  </p>
                ) : null}
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    data-testid={`take-over-${h.id}`}
                    onClick={() => {
                      upsertAIConversation({
                        ...h,
                        state: 'resolved',
                        lastMessageAt: h.lastMessageAt,
                      });
                      navigate(`/customer/${h.customerId}`, {
                        state: { handoffFromAI: true },
                      });
                    }}
                    className="btn-primary text-xs"
                  >
                    <MessageSquare size={10} /> Take over
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      upsertAIConversation({
                        ...h,
                        state: 'qualifying',
                        flags: { ...h.flags, handoffReason: undefined },
                      })
                    }
                    className="btn-ghost text-xs"
                  >
                    <HandshakeIcon size={10} /> Return to AI
                  </button>
                </div>
              </div>
            );
          })}
        </aside>
      </div>
    </section>
  );
}

function Pill({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div
      className={clsx(
        'px-4 py-2 rounded-lg border',
        accent ? 'border-blood/50 bg-blood/10' : 'border-gold-dim/30 bg-ink-deep',
      )}
    >
      <p className="font-cinzel text-lg text-cream">{value}</p>
      <p className="label-small-caps mt-0.5">{label}</p>
    </div>
  );
}

void Sparkles;
